import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Button from "./ui/Button";

interface LateBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LateBookingModal: React.FC<LateBookingModalProps> = ({
  isOpen,
  onClose,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-lg w-full"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b bg-red-50 rounded-t-2xl">
                <div className="flex items-center space-x-3">
                  <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
                  <h2 className="text-xl font-bold text-gray-900 leading-tight">
                    Booking Notice
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-red-100 rounded-full transition-colors"
                >
                  <XMarkIcon className="h-6 w-6 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="p-8 space-y-6">
                <div className="text-center space-y-4">
                  <p className="text-xl font-bold text-gray-900 leading-relaxed">
                    To make a booking for today or tomorrow, please come into
                    our office in person or call us.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6 space-y-4 border border-gray-100">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">
                      Visit our Office
                    </h3>
                    <p className="text-gray-600">
                      Located on the snow at Iimori Goryu Resort.
                      <br />
                      <a
                        href="https://www.hakubasnowsports.com/bus-timetable-and-location"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline font-medium mt-1 inline-block"
                      >
                        View Location and Access Information
                      </a>
                    </p>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      Call Us
                    </h3>
                    <p className="text-gray-600">
                      Call Daniel anytime between 7am and 7pm Japan time:
                      <br />
                      <a
                        href="tel:+8108053576420"
                        className="text-blue-600 hover:text-blue-800 font-bold text-lg block mt-1"
                      >
                        +81 080-5357-6420
                      </a>
                    </p>
                  </div>
                </div>

                <div className="pt-2">
                  <Button
                    onClick={onClose}
                    className="w-full text-lg py-3"
                    variant="primary"
                  >
                    Go Back & Select Another Date
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default LateBookingModal;
